import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { queryKeys } from '@/lib/react-query'
import { logger } from '@/lib/logger'
import type { Usuario, Igreja } from '@/types'

type SocialProvider = 'google' | 'apple'

// Estado global para controlar a criação de perfil
let isCreatingProfile = false
let lastProcessedUserId: string | null = null

export function useAuth() {
  const queryClient = useQueryClient()
  const { user, perfil, setUser, setPerfil, setIsLoading, logout: storeLogout } = useAuthStore()

  // Get current session
  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: queryKeys.auth.session,
    queryFn: async () => {
      if (!supabase) return null

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()

        // Log session status for debugging
        console.log('🔵 [SESSION] Session query result:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          email: session?.user?.email
        })

        return session
      } catch (error) {
        console.error('❌ [SESSION] Error getting session:', error)
        return null
      }
    },
    refetchOnWindowFocus: false, // Evita re-fetch ao mudar visibilidade da página
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (renamed from cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  })

  // Get user profile from usuarios table
  const { data: userPerfil, isLoading: isPerfilLoading } = useQuery({
    queryKey: queryKeys.auth.perfil(session?.user?.id || ''),
    queryFn: async () => {
      if (!session?.user?.id || !supabase) return null

      console.log('🔵 [PERFIL] Fetching user profile for:', session.user.id)

      try {
        const { data, error } = await supabase
          .from('usuarios')
          .select(`
            *,
            igreja:igrejas(*)
          `)
          .eq('auth_user_id', session.user.id)
          .maybeSingle()

        if (error) {
          console.error('❌ [PERFIL] Error fetching user profile:', error)
          throw error
        }

        console.log('🔵 [PERFIL] Profile query result:', {
          hasProfile: !!data,
          profileId: data?.id,
          churchId: data?.igreja_id,
          userName: data?.nome
        })

        // Se não encontrou perfil e tem usuário, tentar novamente em 1s
        if (!data && session?.user?.id) {
          console.log('🔵 [PERFIL] Profile not found, retrying in 1s...')
          await new Promise(resolve => setTimeout(resolve, 1000))

          const { data: retryData, error: retryError } = await supabase
            .from('usuarios')
            .select(`
              *,
              igreja:igrejas(*)
            `)
            .eq('auth_user_id', session.user.id)
            .maybeSingle()

          if (retryError) {
            console.error('❌ [PERFIL] Error on retry:', retryError)
          }

          console.log('🔵 [PERFIL] Retry result:', {
            hasProfile: !!retryData,
            profileId: retryData?.id
          })

          return retryData as Usuario & { igreja?: Igreja } | null
        }

        return data as Usuario & { igreja?: Igreja } | null
      } catch (error) {
        console.error('❌ [PERFIL] Profile query failed:', error)
        throw error
      }
    },
    enabled: !!session?.user?.id && !!supabase,
    refetchOnWindowFocus: false, // Evita re-fetch ao mudar visibilidade da página
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  // Update store when session or profile changes
  useEffect(() => {
    if (session?.user) {
      setUser(session.user)
    } else {
      setUser(null)
    }
  }, [session, setUser])

  useEffect(() => {
    if (userPerfil) {
      setPerfil(userPerfil)
    } else if (userPerfil === null && session?.user) {
      // Se o perfil é null mas há uma sessão, limpar o perfil no store
      setPerfil(null)
    }
  }, [userPerfil, setPerfil, session])

  useEffect(() => {
    setIsLoading(isSessionLoading || isPerfilLoading)
  }, [isSessionLoading, isPerfilLoading, setIsLoading])

  // Listen to auth state changes
  useEffect(() => {
    if (!supabase) return

    console.log('🔵 [AUTH_LISTENER] Setting up auth state change listener')

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`🔵 [AUTH_LISTENER] Auth state change: ${event}`, {
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id
      })
      
      logger.info(`Auth state change: ${event}`, { context: 'AUTH_STATE_CHANGE' })

      if (event === 'SIGNED_IN' && session?.user && supabase) {
        console.log('🔵 [AUTH_LISTENER] Processing SIGNED_IN event')

        // Verificar se estamos em um fluxo OAuth através de múltiplos indicadores
        const hasOAuthFlag = typeof window !== 'undefined' && localStorage.getItem('oauth_flow_active') === 'true'
        const hasOAuthHash = typeof window !== 'undefined' && window.location.hash.includes('access_token')
        const isOAuthProvider = session.user.app_metadata?.provider &&
          ['google', 'apple', 'facebook', 'github'].includes(session.user.app_metadata.provider)
        const isCallbackPage = typeof window !== 'undefined' && window.location.pathname === '/auth/callback'

        // Se tem a flag OU está na callback page com OAuth provider, é OAuth flow
        const isOAuthFlow = hasOAuthFlag || hasOAuthHash || (isOAuthProvider && isCallbackPage)

        if (isOAuthFlow) {
          console.log('🔵 [AUTH_LISTENER] OAuth flow detectado - pulando verificação de perfil', {
            hasOAuthFlag,
            hasOAuthHash,
            isOAuthProvider,
            isCallbackPage,
            provider: session.user.app_metadata?.provider,
            pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
            hash: typeof window !== 'undefined' ? window.location.hash.substring(0, 50) + '...' : 'N/A'
          })
          console.log('🔵 [AUTH_LISTENER] O callback page irá lidar com a criação do perfil')

          // Se não está na callback page mas tem flag OAuth, redirecionar para callback
          if (!isCallbackPage && hasOAuthFlag && typeof window !== 'undefined') {
            console.log('🔵 [AUTH_LISTENER] Flag OAuth ativa mas não está na callback page, redirecionando...')
            window.location.href = '/auth/callback' + window.location.hash
            return
          }

          setUser(session.user)
          return
        }

        setUser(session.user)

        // Evitar processar o mesmo usuário múltiplas vezes, mas permitir reprocessamento após F5
        if (lastProcessedUserId === session.user.id) {
          console.log('🔵 [AUTH_LISTENER] User already processed, checking profile data')
          
          // Check if we have the profile data, if not, continue processing
          const currentPerfil = queryClient.getQueryData(queryKeys.auth.perfil(session.user.id))
          if (currentPerfil) {
            console.log('🔵 [AUTH_LISTENER] Profile data exists, skipping processing')
            return
          } else {
            console.log('🔵 [AUTH_LISTENER] No profile data found, forcing profile reload')
            // Apenas refetch, não precisa invalidar
            queryClient.refetchQueries({ queryKey: queryKeys.auth.perfil(session.user.id), exact: true })
            return
          }
        }

        console.log('🔵 [AUTH_LISTENER] Setting lastProcessedUserId to:', session.user.id)
        lastProcessedUserId = session.user.id

        // Verificar se já temos o perfil no cache antes de invalidar
        const existingProfile = queryClient.getQueryData(['perfil', session.user.id])
        if (existingProfile) {
          console.log('🔵 [AUTH_LISTENER] Profile already exists in cache, skipping invalidation')
          return
        }

        // Apenas fazer refetch direto ao invés de invalidar (mais eficiente)
        console.log('🔵 [AUTH_LISTENER] Fetching profile directly')

        try {
          // Fazer refetch direto sem invalidar
          await queryClient.refetchQueries({
            queryKey: ['perfil', session.user.id],
            exact: true
          })

          console.log('🔵 [AUTH_LISTENER] Profile query fetched successfully')
        } catch (error) {
          console.error('🔴 [AUTH_LISTENER] Error fetching profile:', error)
        }

        // Aguardar um pouco para as queries serem recarregadas
        setTimeout(async () => {
          const currentProfile = queryClient.getQueryData(['perfil', session.user.id])
          console.log('🔵 [AUTH_LISTENER] Profile after invalidation:', {
            hasProfile: !!currentProfile,
            profileData: currentProfile
          })
          
          // Se ainda não há perfil no cache, verificar se realmente não existe no banco
          if (!currentProfile && typeof window !== 'undefined' && !isCreatingProfile) {
            console.log('🔵 [AUTH_LISTENER] Profile not in cache, checking database directly')
            
            try {
              // Verificar se o perfil realmente não existe no banco
              if (!supabase) {
                console.error('🔴 [AUTH_LISTENER] Supabase client not available')
                return
              }

              const { data: existingProfile, error: checkError } = await supabase
                .from('usuarios')
                .select('id')
                .eq('auth_user_id', session.user.id)
                .maybeSingle()

              if (checkError) {
                console.error('🔴 [AUTH_LISTENER] Error checking profile existence:', checkError)
                return
              }

              if (existingProfile) {
                console.log('🔵 [AUTH_LISTENER] Profile exists in database, just not in cache.')
                // Perfil existe, fazer refetch direto
                queryClient.refetchQueries({ queryKey: ['perfil', session.user.id], exact: true })
                return
              }

              // Perfil não existe no banco de dados
              console.log('🔵 [AUTH_LISTENER] Profile does not exist in database. User needs to be registered by admin.')
              // Não criar automaticamente - usuário precisa ser registrado pelo admin
              // Fazer logout para que o usuário seja redirecionado para login
              if (!supabase) {
                console.error('🔴 [AUTH_LISTENER] Supabase client not available for signOut')
                return
              }
              
              try {
                // Verificar se ainda há uma sessão válida antes de tentar logout
                const { data: { session: currentSession } } = await supabase.auth.getSession()
                if (currentSession) {
                  console.log('🔵 [AUTH_LISTENER] Valid session found, attempting logout')
                  await supabase.auth.signOut()
                } else {
                  console.log('🔵 [AUTH_LISTENER] No valid session found, skipping logout')
                  // Limpar dados locais manualmente se não há sessão
                  storeLogout()
                  queryClient.removeQueries({ queryKey: queryKeys.auth.session })
                  queryClient.removeQueries({ queryKey: ['auth', 'perfil'] })
                }
              } catch (logoutError) {
                console.error('🔴 [AUTH_LISTENER] Error during logout attempt:', logoutError)
                // Se falhar o logout, limpar dados localmente
                storeLogout()
                queryClient.removeQueries({ queryKey: queryKeys.auth.session })
                queryClient.removeQueries({ queryKey: ['auth', 'perfil'] })
              }
              return
            } catch (error) {
              console.error('🔴 [AUTH_LISTENER] Error during profile existence check:', error)
              return
            }

            try {
              // Criar usuário
              if (!session || !supabase) {
                logger.error('Session ou Supabase não disponível para criação de usuário', { context: 'AUTH_STATE_CHANGE' })
                isCreatingProfile = false
                return
              }

              // Obter igreja padrão (primeira igreja disponível)
              const { data: igrejas } = await supabase!.from('igrejas').select('id').limit(1)
              const igrejaId = igrejas?.[0]?.id

              if (!igrejaId) {
                logger.error('Nenhuma igreja encontrada para associar ao usuário', { context: 'AUTH_STATE_CHANGE' })
                isCreatingProfile = false
                return
              }

              const newUserData = {
                auth_user_id: session!.user.id,
                nome: session!.user.user_metadata?.full_name || session!.user.email?.split('@')[0] || 'Usuário',
                email: session!.user.email || '',
                telefone: session!.user.user_metadata?.phone || '',
                igreja_id: igrejaId,
                permissao: 'membro' as const,
                ativo: true,
                primeiro_acesso: false,
              }

              const { error: insertError } = await supabase!
                .from('usuarios')
                .insert(newUserData)

              if (insertError) {
                logger.error('Erro ao criar perfil OAuth', { context: 'AUTH_STATE_CHANGE' })
                isCreatingProfile = false
                // Fazer logout se falhar ao criar usuário
                await supabase!.auth.signOut()
              } else {
                logger.info('Perfil OAuth criado com sucesso', { context: 'AUTH_STATE_CHANGE' })
                isCreatingProfile = false
                // Aguardar um pouco antes de invalidar para garantir que foi criado
                await new Promise(resolve => setTimeout(resolve, 500))
                queryClient.invalidateQueries({ queryKey: queryKeys.auth.perfil(session!.user.id) })
              }
            } catch (error) {
              logger.error('Erro ao processar criação de usuário OAuth', { context: 'AUTH_STATE_CHANGE' })
              console.error('Detalhes do erro:', error)
              isCreatingProfile = false
            }
          }
        }, 1000) // Aguardar 1 segundo para as queries serem processadas
      } else if (event === 'SIGNED_OUT') {
          console.log('🔵 [AUTH_LISTENER] Processing SIGNED_OUT event')
          logger.info('Logout detectado', { context: 'AUTH_STATE_CHANGE' })
          lastProcessedUserId = null
          isCreatingProfile = false
          storeLogout()
          
          // Don't clear all queries immediately, just auth-related ones
          queryClient.removeQueries({ queryKey: queryKeys.auth.session })
          queryClient.removeQueries({ queryKey: ['auth', 'perfil'] })
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('🔵 [AUTH_LISTENER] Token refreshed, invalidating session')
          queryClient.invalidateQueries({ queryKey: queryKeys.auth.session })
        } else if (event === 'INITIAL_SESSION') {
          console.log('🔵 [AUTH_LISTENER] Initial session event')
          // Reset processing state on initial session to allow fresh processing
          if (!session?.user) {
            console.log('🔵 [AUTH_LISTENER] No user in initial session, resetting state')
            lastProcessedUserId = null
            isCreatingProfile = false
          }
        }
    })

    return () => {
      console.log('🔵 [AUTH_LISTENER] Cleaning up auth state change listener')
      subscription.unsubscribe()
    }
  }, [setUser, storeLogout, queryClient])

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      if (!supabase) throw new Error('Supabase client not available')

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.session })
    },
  })

  const loginWithProviderMutation = useMutation({
    mutationFn: async (provider: SocialProvider) => {
      if (!supabase) throw new Error('Supabase client not available')

      try {
        logger.info(`Iniciando login social com ${provider}`, { context: 'SOCIAL_LOGIN' })

        // Marcar que estamos iniciando um fluxo OAuth ANTES do redirect
        if (typeof window !== 'undefined') {
          localStorage.setItem('oauth_flow_active', 'true')
          console.log('🔵 [LOGIN_PROVIDER] Flag oauth_flow_active definida')
        }

        // Construir URL de redirecionamento
        const redirectTo = typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback`
          : undefined

        console.log('🔵 [LOGIN_PROVIDER] Redirect URL configurada:', redirectTo)
        console.log('🔵 [LOGIN_PROVIDER] window.location.origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A')

        // Iniciar OAuth
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: redirectTo ? { redirectTo } : undefined,
        })

        console.log('🔵 [LOGIN_PROVIDER] OAuth data.url:', data?.url)
        console.log('🔵 [LOGIN_PROVIDER] OAuth error:', error)

        if (error) {
          // Limpar flag em caso de erro
          if (typeof window !== 'undefined') {
            localStorage.removeItem('oauth_flow_active')
          }
          throw error
        }

        if (typeof window !== 'undefined' && data?.url) {
          logger.info('Redirecionando para OAuth', { context: 'SOCIAL_LOGIN' })
          window.location.href = data.url
        }

        return data
      } catch (error) {
        console.error('Erro no login social:', error)
        // Garantir que a flag seja limpa em caso de erro
        if (typeof window !== 'undefined') {
          localStorage.removeItem('oauth_flow_active')
        }
        throw error
      }
    },
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      logger.info('Iniciando logout', { context: 'LOGOUT' })

      if (!supabase) {
        console.warn('Supabase client não disponível, apenas limpando estado local')
        return
      }

      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (session) {
          const { error } = await supabase.auth.signOut()
          if (error) {
            console.warn('Erro no logout do servidor:', error.message)
          }
        }
      } catch (error) {
        console.warn('Erro ao verificar/fazer logout:', error)
      }
    },
    onSettled: () => {
      // Sempre limpar estado local, independente do resultado
      logger.info('Limpando estado local', { context: 'LOGOUT' })
      lastProcessedUserId = null
      isCreatingProfile = false
      storeLogout()
      queryClient.clear()
    },
  })

  // Update profile mutation
  const updatePerfilMutation = useMutation({
    mutationFn: async (data: Partial<Pick<Usuario, 'nome' | 'telefone'>>) => {
      if (!supabase || !perfil?.id) throw new Error('Supabase client or profile not available')

      const { data: updatedData, error } = await supabase
        .from('usuarios')
        .update(data)
        .eq('id', perfil.id)
        .select(`
          *,
          igreja:igrejas(*)
        `)
        .single()

      if (error) throw error
      return updatedData as Usuario & { igreja?: Igreja }
    },
    onSuccess: (updatedPerfil) => {
      setPerfil(updatedPerfil)
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.perfil(user?.id || '') })
    },
  })

  return {
    user,
    perfil,
    isLoading: isSessionLoading || isPerfilLoading,
    isAuthenticated: !!user && !!perfil,
    isPrimeiroAcesso: !!perfil?.primeiro_acesso,
    login: loginMutation.mutateAsync,
    loginWithProvider: loginWithProviderMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    updatePerfil: updatePerfilMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isLoggingWithProvider: loginWithProviderMutation.isPending,
    isUpdatingPerfil: updatePerfilMutation.isPending,
  }
}

export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth()
  return { isAuthenticated, isLoading }
}

export function usePermission(requiredRole?: Usuario['permissao'] | Usuario['permissao'][]) {
  const { perfil } = useAuth()

  if (!requiredRole) return true
  if (!perfil) return false

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole]
  return roles.includes(perfil.permissao)
}
